import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { Chip, Grid, Checkbox, IconButton, ListItemText, Menu, MenuItem } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import AddIcon from '@material-ui/icons/Add';
import { find } from 'lodash';

import { UserSegmentFragment, ManualNotificationsItemUpdateInput } from 'api';

interface IProps {
  segments: UserSegmentFragment[];
}

const useStyles = makeStyles(theme => ({
  selectedContainer: {
    '& > div': {
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
  },
  item: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  iconButton: {
    marginTop: -theme.spacing(1),
    padding: 0,
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  icon: {
    color: theme.palette.common.white,
    fontSize: theme.typography.pxToRem(32),
  },
  companyChip: {
    borderColor: theme.palette.primary.main,
  },
  segmentChip: {
    borderColor: theme.palette.primary.main,
  },
}));

const Messages: FC<IProps> = ({ segments }) => {
  const classes = useStyles();
  const { values, setFieldValue } = useFormikContext<ManualNotificationsItemUpdateInput>();
  const [anchorEl, setAnchorEl] = useState(null);
  const { t } = useTranslation();

  const { entire_company, segment_ids } = values;

  function isSegmentSelected(segment: UserSegmentFragment) {
    return segment_ids.includes(segment.id);
  }

  function handleCompanyToggle() {
    setFieldValue('entire_company', !entire_company);

    if (!entire_company && segment_ids.length) {
      setFieldValue('segment_ids', []);
    }
  }

  function handleSegmentToggle(segment: UserSegmentFragment) {
    const segmentIdIndex = segment_ids.indexOf(segment.id);

    segmentIdIndex >= 0 ? segment_ids.splice(segmentIdIndex, 1) : segment_ids.push(segment.id);

    setFieldValue('entire_company', !segment_ids.length);
    setFieldValue('segment_ids', segment_ids);
  }

  function renderCompanyChip() {
    if (!entire_company) {
      return null;
    }

    return (
      <Chip
        variant="outlined"
        label={t('components:onboardedUsers')}
        className={classes.companyChip}
      />
    );
  }

  function renderSegmentChip(segment: UserSegmentFragment) {
    return (
      <Chip
        key={segment.id}
        variant="outlined"
        className={classes.segmentChip}
        label={segment.title}
        onDelete={() => handleSegmentToggle(segment)}
        deleteIcon={<CloseIcon fontSize="small" />}
      />
    );
  }

  function renderSegmentChips() {
    if (!segment_ids.length) {
      return null;
    }

    return segment_ids.reduce((list, id) => {
      const segment = find(segments, ['id', id]);
      return list.concat([renderSegmentChip(segment)]);
    }, []);
  }

  function renderSegmentItem(segment: UserSegmentFragment) {
    return (
      <MenuItem key={segment.id} onClick={() => handleSegmentToggle(segment)}>
        <ListItemText primary={segment.title} />
        <Checkbox checked={isSegmentSelected(segment)} />
      </MenuItem>
    );
  }

  function renderMenu() {
    return (
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
        <MenuItem
          className={classes.item}
          onClick={handleCompanyToggle}
          disabled={!segment_ids.length}
        >
          <ListItemText primary={t('components:onboardedUsers')} />
          <Checkbox checked={entire_company} />
        </MenuItem>
        {segments.map(renderSegmentItem)}
      </Menu>
    );
  }

  return (
    <Fragment>
      {renderMenu()}
      <Grid container alignItems="center" spacing={2}>
        <Grid item>
          <IconButton
            className={classes.iconButton}
            color="primary"
            onClick={event => setAnchorEl(event.currentTarget)}
          >
            <AddIcon className={classes.icon} />
          </IconButton>
        </Grid>
        <Grid item xs>
          <div className={classes.selectedContainer}>
            {renderCompanyChip()}
            {renderSegmentChips()}
          </div>
        </Grid>
      </Grid>
    </Fragment>
  );
};

export default Messages;
